import React from 'react';
import {
	JiraIcon,
	JiraServiceManagementIcon,
	JiraProductDiscoveryIcon,
	CustomerServiceManagementIcon,
	type LogoProps,
} from '@atlaskit/logo';
import type {
	LogoProps as TempLogoProps,
	IconProps as TempIconProps,
} from '@atlaskit/temp-nav-app-icons/types';
import type { ProductName } from '@atlassian/jira-shared-types/src/product-name.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import messages from './messages.tsx';

export const iconMap = {
	jira: { icon: JiraIcon, name: 'Jira' },
	serviceDesk: {
		icon: JiraServiceManagementIcon,
		name: 'Jira Service Management',
	},
	'product-discovery': {
		icon: JiraProductDiscoveryIcon,
		name: 'Jira Product Discovery',
	},
	'customer-service': {
		icon: CustomerServiceManagementIcon,
		name: 'Customer Service Management',
	},
} as const;

/**
 * Due to typescript limitations (props type clashes), we need to handle the icon and logo components differently for the
 * new and old navigation apps. This function returns the appropriate components based on the product name.
 */
export function getProductIcon(productName: ProductName, testIdPrefix?: string, label?: string) {
	const productKey = productName === 'core' || productName === 'software' ? 'jira' : productName;

	const { icon: NewIconComponent, name: logoName } = iconMap[productKey];

	const createComponent =
		<P extends LogoProps | TempIconProps | TempLogoProps>(
			Component: React.ComponentType<P>,
			type: 'icon' | 'logo',
			shouldUseNewLogoDesign?: boolean,
		) =>
		(props: P) => {
			const additionalProps: Partial<P> & { shouldUseNewLogoDesign?: boolean } = {};
			// Nav v3 sets a testId, Nav v4 sets a label
			if (testIdPrefix) {
				additionalProps.testId = `${testIdPrefix}-${type}`;
			}
			if (label) {
				additionalProps.label = label;
			}
			additionalProps.shouldUseNewLogoDesign = shouldUseNewLogoDesign;
			// Casting required due to complexities of the different component prop types
			return (
				<Component
					{...props}
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					{...(additionalProps as P)}
				/>
			);
		};

	const newIcon = createComponent<LogoProps>(NewIconComponent, 'icon', true);

	return { icon: newIcon, name: logoName };
}

// formatMessage is intentionally an any type due to absence of easily importable type, and because actual
// type definition contains any as well!
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getLogoLabel(formatMessage: any, productName: ProductName): string {
	let label = '';
	if (productName === 'serviceDesk') {
		label = formatMessage(messages.jsmLogoLabel);
	} else if (productName === 'product-discovery') {
		label = formatMessage(messages.jpdLogoLabel);
	} else if (productName === 'customer-service') {
		label = formatMessage(
			fg('csm_rename_jcs_to_csm') ? messages.csmLogoLabel : messages.jcsLogoLabel,
		);
	} else {
		label = formatMessage(messages.jiraLogoLabel);
	}
	return label;
}
